import { ActionTree, GetterTree, Module, MutationTree } from "vuex";
import { IDepartmentState } from "./types";
import { IIDAStore } from "../types";
import { departmentService } from "../../services/department.service";
import {
  Department,
  IDepartment,
  IDepartmentPost
} from "../../models/department";
import { DepartmentEmployee } from "../../models/departmentEmployee";

export const getInitialState = (): IDepartmentState => {
  return {
    departments: [],
    departmentEmployees: []
  };
};

const namespaced = true;

export const getters: GetterTree<IDepartmentState, IIDAStore> = {
  GetDepartments: (state) => {
    return state.departments;
  },
  GetDepartmentEmployees: (state) => {
    return state.departmentEmployees;
  },
  GetDepartmentEmployeesByDepartmentId: (state) => (departmentId: number) => {
    const list = state.departmentEmployees.filter((a) => a.department_id === departmentId);
    return list;
  },
  GetActiveDepartments: (state) => () => {
    return state.departments.filter((d) => d.is_active === true);
  },
  IsEmployeeMemberOfDepartment: (state) => (employee_id: number, department_id: number) => {
    return state.departmentEmployees.find(
      (d) => d.department_id == department_id && d.employee_id == employee_id
    );
  },
  GetEmployeesInDepartments:
    (state) =>
      (departmentIds: number[]): number[] => {
        const EmployeesID: number[] = state.departmentEmployees
          .filter((de) => departmentIds.includes(de.department_id))
          .map((d) => d.employee_id);
        return Array.from(new Set(EmployeesID));
      },
  GetPrimaryDepartmentByEmployeeId:
        (state) =>
          (employeeId: number): Department | null => {
            const primaryDepartmentEmployee: DepartmentEmployee | undefined = state.departmentEmployees
              .find((de) => de.employee_id == employeeId && de.primary_department);

            if (primaryDepartmentEmployee) {
              return state.departments.find((d) => d.id == primaryDepartmentEmployee.department_id) ?? null;
            }
            return null;
          },
  GetDepartmentsByEmployeeId:
    (state) =>
      (employeeId: number): Department[] => {
        const departments: Department[] = state.departmentEmployees
          .filter((de) => de.employee_id == employeeId)
          .map((d) => state.departments.find((de) => de.id == d.department_id)!);
        return departments;
      },
  GetDepartmentById:
    (state) =>
      (departmentId: number): Department | undefined => {
        return state.departments.find((d) => d.id === departmentId);
      }
};

// Module-level in-flight promises so concurrent dispatches share a single
// network request instead of each kicking off its own fetch when the Vuex
// state is still empty.
let departmentsInFlight: Promise<Department[]> | null = null;
let departmentEmployeesInFlight: Promise<DepartmentEmployee[]> | null = null;

export const actions: ActionTree<IDepartmentState, IIDAStore> = {
  reset({ commit }) {
    commit("reset");
  },
  deleteDepartment({ commit }, departmentId: number): Promise<any> {
    return new Promise((resolve, reject) => {
      departmentService
        .deleteDepartments(departmentId)
        .then((response) => {
          commit("deleteDepartment", departmentId);
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  createDepartment({ commit }, department: IDepartmentPost): Promise<IDepartment> {
    return new Promise((resolve, reject) => {
      departmentService
        .createDepartments(department)
        .then((newDepartment: Department) => {
          commit("updateDepartments", { departments: [newDepartment] });
          resolve(newDepartment);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  updateDepartment({ commit }, department: Department): Promise<Department> {
    return new Promise((resolve, reject) => {
      departmentService
        .updateDepartments(department)
        .then((newDepartment: Department) => {
          commit("updateDepartments", { departments: [newDepartment] });
          resolve(newDepartment);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  fetchDepartments({ state, commit }, update = false): Promise<Department[]> {
    if (state.departments.length > 0 && !update) {
      return Promise.resolve(state.departments);
    }
    if (departmentsInFlight && !update) {
      return departmentsInFlight;
    }
    const promise = departmentService
      .fetchDepartments()
      .then((departments: Department[]) => {
        commit("updateDepartments", { departments });
        return departments;
      })
      .finally(() => {
        if (departmentsInFlight === promise) {
          departmentsInFlight = null;
        }
      });
    departmentsInFlight = promise;
    return promise;
  },
  /**
   *  Department Employees
   *  */

  fetchDepartmentEmployees({ state, commit }, update = false): Promise<DepartmentEmployee[]> {
    if (state.departmentEmployees.length > 0 && !update) {
      return Promise.resolve(state.departmentEmployees);
    }
    if (departmentEmployeesInFlight && !update) {
      return departmentEmployeesInFlight;
    }
    const promise = departmentService
      .fetchDepartmentEmployees()
      .then((departmentEmployees: DepartmentEmployee[]) => {
        commit("updateDepartmentEmployees", { departmentEmployees });
        return departmentEmployees;
      })
      .finally(() => {
        if (departmentEmployeesInFlight === promise) {
          departmentEmployeesInFlight = null;
        }
      });
    departmentEmployeesInFlight = promise;
    return promise;
  },
  SaveDepartmentEmployees(
    { dispatch, state },
    payload: { departmentId: number; employees: {employee_id: number, primary_department: boolean}[]}
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const currentEmployees: DepartmentEmployee[] = state.departmentEmployees.filter(
          (de) => de.department_id == payload.departmentId
        );
        const promiseArray: Promise<DepartmentEmployee>[] = [];
        // Find employees to create
        for (let index = 0; index < payload.employees.length; index++) {
          const employee = payload.employees[index];
          if (!currentEmployees.find((ce) => ce.employee_id == employee.employee_id)) {
            // Create department employee
            promiseArray.push(
              dispatch("createDepartmentEmployees", {
                departmentId: payload.departmentId,
                employeeId: employee.employee_id,
                primaryDepartment: employee.primary_department
              })
            );
          } else {
            // Update department employee
            promiseArray.push(
              dispatch("updateDepartmentEmployees", {
                departmentId: payload.departmentId,
                employeeId: employee.employee_id,
                primaryDepartment: employee.primary_department
              })
            );
          }
        }
        // Remove employees
        const employeeIdsDelete = currentEmployees.filter(
          (ce) => !payload.employees.some((e) => e.employee_id == ce.employee_id)
        );
        for (let index = 0; index < employeeIdsDelete.length; index++) {
          const element = employeeIdsDelete[index];
          promiseArray.push(dispatch("deleteDepartmentEmployees", { employeeId: element.id }));
        }

        Promise.all(promiseArray).then(() => {
          resolve();
        });
      } catch (error) {
        return reject(error);
      }
    });
  },
  createDepartmentEmployees(
    { commit },
    payload: { departmentId: number; employeeId: number; primaryDepartment: boolean }
  ): Promise<DepartmentEmployee> {
    return new Promise((resolve, reject) => {
      departmentService
        .createDepartmentEmployee(payload.departmentId, payload.employeeId, payload.primaryDepartment)
        .then((newDepartmentEmployee: DepartmentEmployee) => {
          commit("updateDepartmentEmployees", { departmentEmployees: [newDepartmentEmployee] });
          resolve(newDepartmentEmployee);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  updateDepartmentEmployees(
    { commit, state },
    payload: { departmentId: number; employeeId: number; primaryDepartment: boolean }
  ): Promise<DepartmentEmployee> {
    return new Promise((resolve, reject) => {
      // Find the existing department employee record
      const existingDepartmentEmployee = state.departmentEmployees.find(
        (de) => de.department_id === payload.departmentId && de.employee_id === payload.employeeId
      );

      if (!existingDepartmentEmployee) {
        reject(new Error("Department employee not found"));
        return;
      }

      departmentService
        .updateDepartmentEmployee(existingDepartmentEmployee.id, payload.primaryDepartment)
        .then((updatedDepartmentEmployee: DepartmentEmployee) => {
          commit("updateDepartmentEmployees", { departmentEmployees: [updatedDepartmentEmployee] });
          resolve(updatedDepartmentEmployee);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  deleteDepartmentEmployees({ commit }, payload: { employeeId: number }): Promise<any> {
    return new Promise((resolve, reject) => {
      departmentService
        .deleteDepartmentEmployee(payload.employeeId)
        .then((response) => {
          commit("deleteDepartmentEmployees", payload.employeeId);
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  DepartmentEmployeesByDepartmentId(
    { state },
    departmentId: number
  ): Promise<DepartmentEmployee[]> {
    return new Promise((resolve, reject) => {
      try {
        const departmentEmployees = state.departmentEmployees.filter(
          (de) => de.department_id === departmentId
        );
        return resolve(departmentEmployees);
      } catch (error) {
        return reject(error);
      }
    });
  },
  bulkTransferDepartments(
    _context,
    payload: {
      transferDate: Date;
      employeeTransfers: Array<{
        employee_id: number;
        from_department_id: number;
        to_department_id: number | null;
      }>;
    }
  ): Promise<{ success: boolean; moved_duties_count: number }> {
    return new Promise((resolve, reject) => {
      departmentService
        .bulkTransferDepartments(payload.transferDate, payload.employeeTransfers)
        .then((response) => {
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
};

export const mutations: MutationTree<IDepartmentState> = {
  reset(state: IDepartmentState): void {
    Object.assign(state, getInitialState());
  },
  deleteDepartment(state: IDepartmentState, departmentId: number): void {
    state.departments = state.departments.filter((existing) => existing.id !== departmentId);
  },
  updateDepartments(state: IDepartmentState, payload: { departments: Department[] }): void {
    state.departments = [
      ...payload.departments,
      ...state.departments.filter(
        (oldDepartments) =>
          !payload.departments.find((newDepartments) => newDepartments.equals(oldDepartments))
      )
    ];
  },
  updateDepartmentEmployees(
    state: IDepartmentState,
    payload: { departmentEmployees: DepartmentEmployee[] }
  ): void {
    state.departmentEmployees = [
      ...payload.departmentEmployees,
      ...state.departmentEmployees.filter(
        (oldDepartments) =>
          !payload.departmentEmployees.find((newDepartments) =>
            newDepartments.equals(oldDepartments)
          )
      )
    ];
  },
  deleteDepartmentEmployees(state: IDepartmentState, departmentEmployeesId: number): void {
    state.departmentEmployees = state.departmentEmployees.filter(
      (existing) => existing.id !== departmentEmployeesId
    );
  }
};

// Utility function to compare department properties (excluding timestamps and id)
export function hasDepartmentChanged(originalDepartment: Department, updatedDepartment: Department): boolean {
  if (!originalDepartment || !updatedDepartment) {
    return true; // If either is missing, consider it changed
  }
  
  return (
    originalDepartment.name !== updatedDepartment.name ||
    originalDepartment.is_active !== updatedDepartment.is_active ||
    originalDepartment.color !== updatedDepartment.color ||
    originalDepartment.icon_name !== updatedDepartment.icon_name ||
    originalDepartment.main_department !== updatedDepartment.main_department
  );
}

export const departmentsStoreModule: Module<IDepartmentState, IIDAStore> = {
  namespaced: namespaced,
  state: getInitialState(),
  getters: getters,
  actions: actions,
  mutations: mutations
};
